import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { getNestedValue } from 'lib/helpers/pdf/tableExport/lib/extractDataFromColDefs';
import {
  camelToSpaceCase,
  formatLargeNumber,
  ia,
  iaRa,
  isPrimitiveValue,
  mString,
  snakeToTitleCase
} from 'lib/helpers/utility';

import { COLUMN_SAMPLE_SIZE } from './constants';
import { resolveViewportHref } from './resolveViewportHref';

const nonFormattedFields = ['_id', '_code'];

const isNonFormattedField = (key) =>
  nonFormattedFields.some((substring) => key.includes(substring));

const isNumericValue = (value) => !isNaN(parseFloat(value)) && isFinite(value);

// Averages, rates and ages (days_outstanding_ on the AR reports) are measures too, but adding a
// column of them up means nothing. Not every "days" field: ar_over_90_days is an amount.
const NON_ADDITIVE_FIELD_PATTERN = /(^|_)(avg|average|rate|days_outstanding)(_|$)/;

export const isAdditiveField = (key) => !NON_ADDITIVE_FIELD_PATTERN.test(key);

// Numeric identifiers/codes (procedure_code, *_id) render verbatim — never with thousands
// separators — even when the key ends with an underscore. Everything else numeric is a measure:
// counts (trailing "_") via formatLargeNumber, otherwise money via mString. Exports read the same
// rule, so a column is typed and totalled exactly as the grid formats it.
export const getMeasureKind = (key, sampleValue) => {
  if (!isNumericValue(sampleValue) || isNonFormattedField(key)) return null;

  return key.endsWith('_') ? 'count' : 'currency';
};

export const generateHeaderName = (key) => {
  return camelToSpaceCase(snakeToTitleCase(key.replace(/__/g, '/').replace(/_id/g, ' #')));
};

const collectColumnSamples = (data) => {
  const samples = new Map();

  data.slice(0, COLUMN_SAMPLE_SIZE).forEach((row) => {
    Object.entries(row || {}).forEach(([key, value]) => {
      if (key === 'children') return;

      if (!samples.has(key)) samples.set(key, undefined);

      if (samples.get(key) === undefined && value !== null) samples.set(key, value);
    });
  });

  return samples;
};

export const getColumnMeasureKinds = (data) => {
  const kinds = new Map();

  collectColumnSamples(iaRa(data)).forEach((sampleValue, key) => {
    const kind = getMeasureKind(key, sampleValue);
    if (kind) kinds.set(key, kind);
  });

  return kinds;
};

export const generateColumnDefs = (data) => {
  if (!ia(data)) return [];

  const samples = collectColumnSamples(data);
  const keys = [...samples.keys()];

  const columnDefs = keys.map((key) => {
    const sampleValue = samples.get(key);
    const isNumber = isNumericValue(sampleValue);

    const headerName = generateHeaderName(key);

    const colDef = { headerName, field: key };

    // add group row
    if (keys.indexOf(key) === 0 && data.some((item) => ia(item.children))) {
      colDef.cellRenderer = 'agGroupCellRenderer';
    }

    if (sampleValue?.value !== undefined) {
      colDef.valueGetter = (params) => getNestedValue(params.data[key]?.value); // this fixes stuffs like sorting in ag grid or exporting data

      const cellRenderer = (params) => {
        const value = params.data[key]?.value ?? '-';
        const href = params.data[key]?.href;

        // add link if provided
        if (typeof value !== 'object') {
          if (!href) return value;

          return (
            <Link to={resolveViewportHref(href)} target="_blank">
              <span className="text-neutral-900 underline hover:opacity-80">{value}</span>
            </Link>
          );
        }

        const entries = Object.entries(value || {});

        return (
          <>
            {entries.map(([currentKey, currentValue], index) => {
              if (!href) {
                return (
                  <span key={currentKey}>
                    {!currentKey.endsWith('_') && typeof currentValue === 'number'
                      ? mString(currentValue)
                      : currentValue}
                    {index < entries.length - 1 && ' / '}{' '}
                  </span>
                );
              }

              return (
                <Fragment key={currentKey}>
                  <Link to={resolveViewportHref(href?.[currentKey])} target="_blank">
                    <span className="text-neutral-900 underline hover:opacity-80">
                      {currentValue}
                    </span>
                  </Link>
                  {index < entries.length - 1 && ' / '}
                </Fragment>
              );
            })}
          </>
        );
      };

      // avoid missing row expanding
      if (!colDef.cellRenderer) {
        colDef.cellRenderer = cellRenderer;
      } else {
        colDef.cellRendererParams = { innerRenderer: cellRenderer };
      }
    }

    if (isNumber) {
      const measureKind = getMeasureKind(key, sampleValue);

      if (measureKind) {
        const formatNumeric = measureKind === 'count' ? formatLargeNumber : mString;
        colDef.valueFormatter = (params) =>
          typeof params.value === 'string' && isNaN(parseFloat(params.value))
            ? params.value
            : formatNumeric(params.value);
      }
    } else if (isPrimitiveValue(sampleValue) && !isNonFormattedField(key)) {
      // capitalize values
      colDef.valueFormatter = (params) => {
        return camelToSpaceCase(snakeToTitleCase(params?.value?.toString())) || '-';
      };
    } else {
      colDef.valueFormatter = (params) => params.value || '-';
    }

    return colDef;
  });

  return columnDefs;
};
